import { z } from 'zod';
import { CivilDateSchema, RequestStatusSchema } from './requests.js';
/**
 * Analítica (09 §6.10). Sustituye la lógica que hoy se calcula en el cliente (`src/lib/request-analytics.ts`
 * y `src/lib/export-approved-hours-xlsx.ts`). Las horas son la duración efectiva: la real si existe y,
 * si no, la solicitada. Totales y promedios con 2 decimales.
 */
const Count = z.int().min(0);
const Hours = z.number().min(0);
/** Mes calendario de la fecha del permiso (`YYYY-MM`), no el de la decisión. */
export const MonthKeySchema = z.string().regex(/^[0-9]{4}-(0[1-9]|1[0-2])$/);
/** Filtros comunes. Fechas inclusivas sobre la fecha del permiso; `from > to` es 400. */
export const analyticsFilterShape = {
    from: CivilDateSchema.optional(),
    to: CivilDateSchema.optional(),
    areaId: z.uuid().optional(),
    permissionTypeId: z.uuid().optional(),
    employeeId: z.uuid().optional(),
    status: RequestStatusSchema.optional(),
    /** Sin distinguir mayúsculas: número de solicitud, empleado, tipo y aprobador almacenado. */
    q: z.string().trim().min(1).max(100).optional(),
};
const fromNotAfterTo = (value) => value.from === undefined || value.to === undefined || value.from <= value.to;
const FROM_AFTER_TO = { message: 'from_after_to', path: ['to'] };
export const AnalyticsQuerySchema = z.strictObject(analyticsFilterShape).refine(fromNotAfterTo, FROM_AFTER_TO);
/** Umbral de pendientes en horas naturales (D-08 abierta): ≥ 0, hasta 2 decimales; 24 por defecto. */
const ThresholdHoursParam = z
    .string()
    .regex(/^(0|[1-9][0-9]{0,3})(\.[0-9]{1,2})?$/)
    .transform(Number)
    .pipe(z.number().min(0).max(8760));
export const DecisionTimesQuerySchema = z
    .strictObject({ ...analyticsFilterShape, thresholdHours: ThresholdHoursParam.default(24) })
    .refine(fromNotAfterTo, FROM_AFTER_TO);
export const AnalyticsNamedCountSchema = z.strictObject({ id: z.uuid(), name: z.string(), count: Count });
/**
 * Empleado del resumen: `count` son sus solicitudes del conjunto filtrado (todos los estados que deje el
 * filtro) y `approvedHours`, la suma de las horas efectivas de sus `APPROVED`, redondeada una vez (0 si no tiene).
 */
export const AnalyticsEmployeeSummarySchema = z.strictObject({ id: z.uuid(), name: z.string(), count: Count, approvedHours: Hours });
/** Área actual del empleado; `id: null` y nombre «Sin área» si no tiene. */
export const AnalyticsAreaCountSchema = z.strictObject({ id: z.uuid().nullable(), name: z.string(), count: Count });
/** Agregado de `GET /analytics/requests/summary` (no es el `RequestSummary` del listado). */
export const AnalyticsSummarySchema = z.strictObject({
    total: Count,
    /** Siempre los 5 estados, en el orden del flujo. */
    byStatus: z.array(z.strictObject({ status: RequestStatusSchema, count: Count })).length(5),
    byPermissionType: z.array(AnalyticsNamedCountSchema),
    byMonth: z.array(z.strictObject({ month: MonthKeySchema, count: Count })),
    /** Ordenado por nombre y, a igual nombre, por id. */
    byEmployee: z.array(AnalyticsEmployeeSummarySchema),
    byArea: z.array(AnalyticsAreaCountSchema),
    /** Horas efectivas de las solicitudes `APPROVED` del conjunto filtrado. */
    approvedHours: Hours,
});
/** Fila de `GET /analytics/approved-hours`: horas aprobadas de un empleado en un mes. */
export const ApprovedHoursSchema = z.strictObject({
    employeeId: z.uuid(),
    employeeName: z.string(),
    month: MonthKeySchema,
    hours: Hours,
});
export const DecisionTimeMetricSchema = z.strictObject({
    /** Promedio en horas naturales; `null` si no hay duraciones válidas. */
    averageHours: Hours.nullable(),
    validCount: Count,
    eligibleCount: Count,
    coveragePercent: z.number().min(0).max(100),
});
export const DECISION_DURATION_BUCKETS = [
    { key: 'lt1h', minHours: 0, maxHours: 1 },
    { key: 'h1to4', minHours: 1, maxHours: 4 },
    { key: 'h4to8', minHours: 4, maxHours: 8 },
    { key: 'h8to24', minHours: 8, maxHours: 24 },
    { key: 'd1to2', minHours: 24, maxHours: 48 },
    { key: 'd2to3', minHours: 48, maxHours: 72 },
    { key: 'gt3d', minHours: 72, maxHours: null },
];
export const DecisionDurationBucketKeySchema = z.enum(['lt1h', 'h1to4', 'h4to8', 'h8to24', 'd1to2', 'd2to3', 'gt3d']);
/** Intervalo semiabierto `[minHours, maxHours)`; el último no tiene límite superior. */
export const DecisionDurationBucketSchema = z.strictObject({
    key: DecisionDurationBucketKeySchema,
    minHours: Hours,
    maxHours: Hours.nullable(),
    count: Count,
});
export const PendingOverThresholdSchema = z.strictObject({
    thresholdHours: Hours,
    count: Count,
    requestIds: z.array(z.uuid()),
    totalPending: Count,
    withSubmissionCount: Count,
    coveragePercent: z.number().min(0).max(100),
});
/** `GET /analytics/decision-times`: horas naturales de reloj, no hábiles (D-08 abierta). */
export const DecisionTimeMetricsSchema = z.strictObject({
    /** Hora de PostgreSQL con la que se miden las pendientes. */
    measuredAt: z.iso.datetime(),
    overall: DecisionTimeMetricSchema,
    approved: DecisionTimeMetricSchema,
    rejected: DecisionTimeMetricSchema,
    histogram: z.array(DecisionDurationBucketSchema).length(7),
    pendingOverThreshold: PendingOverThresholdSchema,
});
/** Tipo de contenido de la exportación (libro de Excel). */
export const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
//# sourceMappingURL=analytics.js.map